// src/components/dashboard/ClientDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiPlus, FiSearch, FiCalendar, FiDollarSign, FiClock, 
  FiUser, FiMessageSquare, FiTrendingUp, FiCheckCircle,
  FiBriefcase, FiUsers, FiBarChart3, FiFilter, FiEdit,
  FiEye, FiMail
} from 'react-icons/fi';
import { Sparkles, Clock, DollarSign, Briefcase, TrendingUp, UserCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';
import OpenchatWidget from '../chat/OpenchatWidget';

const ClientDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeJobs: 0,
    completedJobs: 0,
    totalSpent: 0,
    avgRating: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [chatOpen, setChatOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);

  useEffect(() => {
    if (userProfile?.organizationId) {
      fetchDashboardData();
    }
  }, [userProfile]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [jobStats, recentJobsData] = await Promise.all([
        jobService.getClientStats(userProfile.organizationId),
        jobService.getRecentJobs(userProfile.organizationId, 5)
      ]);
      
      setStats(jobStats);
      setRecentJobs(recentJobsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'open': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const openJobChat = (job) => {
    if (!job.assignedVA) return;
    
    setSelectedChat({
      recipientId: job.assignedVA,
      recipientName: job.assignedVAName,
      recipientEmail: job.assignedVAEmail,
      jobContext: {
        id: job.id,
        title: job.title,
        status: job.status
      }
    });
    setChatOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with Action Button */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Client Dashboard</h1>
            <p className="mt-2 text-gray-600">Manage your virtual assistant jobs and team</p>
          </div>
          
          <button
            onClick={() => navigate('/jobs/post')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
          >
            <FiPlus className="mr-2" size={20} />
            Post New Job
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <button
          onClick={() => navigate('/jobs/manage', { state: { filter: 'active' } })}
          className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiBriefcase className="text-blue-600" size={24} />
            </div>
            <TrendingUp className="text-green-500" size={20} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.activeJobs}</h3>
          <p className="text-gray-600 text-sm mt-1">Active Jobs</p>
        </button>

        <button
          onClick={() => navigate('/jobs/manage', { state: { filter: 'completed' } })}
          className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiCheckCircle className="text-green-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.completedJobs}</h3>
          <p className="text-gray-600 text-sm mt-1">Completed Jobs</p>
        </button>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiDollarSign className="text-purple-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalSpent)}</h3>
          <p className="text-gray-600 text-sm mt-1">Total Spent</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Sparkles className="text-yellow-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.avgRating.toFixed(1)}</h3>
          <p className="text-gray-600 text-sm mt-1">Average Rating</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <button
          onClick={() => navigate('/jobs/post')}
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-6 hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <FiPlus size={24} />
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-1">Post a Job</h3>
          <p className="text-blue-100 text-sm">Create a new job posting for VAs</p>
        </button>

        <button
          onClick={() => navigate('/jobs/manage')}
          className="bg-white border rounded-lg p-6 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <FiBriefcase className="text-gray-700" size={24} />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Manage Jobs</h3>
          <p className="text-gray-600 text-sm">View and update all your jobs</p>
        </button>

        <button
          onClick={() => navigate('/vas/browse')}
          className="bg-white border rounded-lg p-6 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <FiUsers className="text-gray-700" size={24} />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Browse VAs</h3>
          <p className="text-gray-600 text-sm">Find skilled virtual assistants</p>
        </button>
      </div>

      {/* Recent Jobs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Recent Jobs</h2>
          <button
            onClick={() => navigate('/jobs/manage')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
          >
            View All
            <FiSearch className="ml-1" size={16} />
          </button>
        </div>

        {recentJobs.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {recentJobs.map((job) => (
              <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-gray-900 truncate">{job.title}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <FiUser size={14} className="mr-1" />
                        {job.assignedVA ? job.assignedVA.name : 'Unassigned'}
                      </div>
                      <div className="flex items-center">
                        <FiCalendar size={14} className="mr-1" />
                        {new Date(job.createdAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center">
                        <FiDollarSign size={14} className="mr-1" />
                        {formatCurrency(job.budget)}
                      </div>
                      {job.deadline && (
                        <div className="flex items-center">
                          <Clock size={14} className="mr-1" />
                          Due {new Date(job.deadline).toLocaleDateString()}
                        </div>
                      )}
                      {job.applications && job.applications.length > 0 && (
                        <div className="flex items-center text-blue-600">
                          <FiUsers size={14} className="mr-1" />
                          {job.applications.length} Application{job.applications.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">

                    {job.assignedVA && (
                      <button
                        onClick={() => openJobChat(job)}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors flex items-center"
                      >
                        <FiMessageSquare size={16} className="mr-2" />
                        Message VA
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center"
                    >
                      <FiEye size={14} className="mr-1" />
                      View
                    </button>
                    
                    <button
                      onClick={() => navigate(`/jobs/edit/${job.id}`)}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center"
                    >
                      <FiEdit size={14} className="mr-1" />
                      Edit
                    </button>
                    
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Briefcase className="text-gray-400" size={24} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs yet</h3>
            <p className="text-gray-600 mb-6">Post your first job to get started with virtual assistance</p>
            <button
              onClick={() => navigate('/jobs/post')}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <FiPlus className="mr-2" size={16} />
              Post Your First Job
            </button>
          </div>
        )}
      </div>

      {chatOpen && selectedChat && (
        <OpenchatWidget
          recipientId={selectedChat.recipientId}
          recipientName={selectedChat.recipientName}
          recipientEmail={selectedChat.recipientEmail}
          jobContext={selectedChat.jobContext}
          isOpen={chatOpen}
          onToggle={() => setChatOpen(!chatOpen)}
          position="bottom-right"
        />
      )}
    </div>
  );
};

export default ClientDashboard;